/* ═══════════════════════════════════════════════════════════════
   App — Main application shell (routing, layout, sidebar)
   Depends on: constants.ts, utils.ts, TokenComponents.tsx,
               EditorComponents.tsx, + page components (Home, Projects, etc.)
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { mkDoc, mkHF, mkSp } from './utils';
import { csiLevelStyles, fillFormaCsiGaps } from '../shared/csi-defaults';
import type { StandardConfig } from '../shared/types';
import { Home } from './pages/Home';
import { Projects } from './pages/Projects';
import { SpecLibrary } from './pages/SpecLibrary';
import { SmartBlocksPage } from './pages/SmartBlocksPage';
import { BlockEditor } from './pages/BlockEditor';
// The Designer owns every PROJECT document. BlockEditor stays the masters
// editor until its own rebuild — see docs/DESIGNER_PLAN.md.
import { Designer } from './designer/Designer';
// The Master Forma Studio (docs/DESIGNER_UNIFIED_PLAN.md §3 R6) is the
// master forma editor — the hand-built classic canvas retired once parity
// was proven (see docs/rebuild history for the phase-A/B/C reports).
import { Studio } from './forma-studio/Studio';
// ModulesPage retired in v2.12.0 — library-module transclusion feature removed.
// Forge removed — replaced by Add Specs (master → project) page
import { AddSpecs } from './pages/AddSpecs';
import { OrganizationsPage } from './pages/OrganizationsPage';
import { TeamPage } from './pages/TeamPage';
import { TimeExpensePage } from './pages/TimeTrackingPage';
// InvoicesPage removed — duplicate of TimeSheetsPage
import { AdminPage } from './pages/AdminPage';
import {
  computeHarSyncPlan,
  applyHarSyncPlan,
  getHarAutoApply,
  canHarSync,
} from './services/harSyncService';
// Old ReportsPage (raw-table CSV/Excel/PDF dump) is kept on disk but
// no longer imported here — superseded by ReportsPageNew below.
import { TasksPage } from './pages/TasksPage';
// New invoicing — separate from the legacy timesheet-approval InvoicesPage.tsx,
// which we leave in place but unrouted (slated for cleanup later).
import InvoicesPageNew from './pages/InvoicesPageNew';
// New canonical-reports page — six built-in reports, filters, charts, exports.
// LegacyReportsPage still imported for the simple-export view (CSV/Excel/PDF
// of raw tables) but unrouted now; we may surface it as a "Raw Export" tab
// under the new ReportsPage in a future patch.
import ReportsPageNew from './pages/ReportsPageNew';
import { LoginPage } from './pages/LoginPage';
import { DesktopHandoff, WebClerkAutoAuth, SpecFormaClerkProvider } from './pages/ClerkSignIn';
import { isDesktopSigninRequest } from './lib/clerkConfig';
import { PublicSite } from './marketing/PublicSite';
import { DesktopOnlyNotice } from './components/DesktopOnlyNotice';
import { isWeb } from './lib/platform';
import { AuthService } from './services/authService';
import type { AuthUser } from './services/authService';
import { setCurrentFirmId } from './services/firmContext';
import { clearAll, list } from './services/cloudCache';
import { Prefs } from './services/prefsService';
import { FirmPrefs } from './services/firmPrefsService';
import { connectLiveSync, disconnectLiveSync } from './services/liveSync';
import { FormaService } from './services/formaService';
import { SpecService } from './services/specService';
import { ProjectSpecService } from './services/projectSpecService';
import { getAllowedNavIds } from '../shared/modules';
import { hasPermission } from '../shared/permissions';
import { NotificationBell } from './components/NotificationBell';
import { TooltipProvider } from './components/ui/tooltip';
import { VerifyEmailBanner } from './components/VerifyEmailBanner';
import { Library, Formatter, Settings } from './components/EditorComponents';
import { OrgService } from './services/orgService';
import { reportError } from './services/errorLog';
import type { DeepLink } from './types/navigation';
import { logPageView } from './services/activityLogger';
import { pushEditHistory } from '../shared/editHistory';

/* ─── Sidebar Navigation Icons ─── */
const NAV_ICONS: Record<string, React.ReactNode> = {
  home: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  projects: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  ),
  'spec-masters': (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <line x1="8" y1="7" x2="16" y2="7" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  ),
  modules: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* stacked cubes — reusable building-blocks motif */}
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  formas: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  ),
  'smart-blocks': (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* sparkle / dynamic content motif */}
      <path d="M12 3l1.9 5.8L20 11l-5.8 1.9L12 19l-1.9-6.1L4 11l6.1-2.2z" />
      <path d="M19 4l.6 1.8L21 6l-1.4.4L19 8l-.6-1.6L17 6l1.4-.2z" />
    </svg>
  ),
  compile: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  ),
  format: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  ),
  settings: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09" />
    </svg>
  ),
  sun: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  ),
  moon: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  ),
  menu: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  user: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{ flexShrink: 0 }}
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  back: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  tbSettings: (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" y1="8" x2="20" y2="8" />
      <circle cx="9" cy="8" r="2" />
      <line x1="4" y1="16" x2="20" y2="16" />
      <circle cx="15" cy="16" r="2" />
    </svg>
  ),
  tbExport: (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3v12" />
      <polyline points="7 10 12 15 17 10" />
      <path d="M5 21h14" />
    </svg>
  ),
  tbSave: (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  tbPencil: (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  ),
  clients: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <circle cx="12" cy="10" r="3" />
      <path d="M8 16c0-2 2-3 4-3s4 1 4 3" />
    </svg>
  ),
  reports: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="12" y2="17" />
      <polyline points="10 9 11 11 14 7" />
    </svg>
  ),
  team: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  'time-expense': (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  invoices: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  tasks: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  admin: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
};

const NAV_ITEMS = [
  { id: 'home', label: 'Home' },
  { id: '_section_pm', label: 'Project Management', section: true },
  { id: 'clients', label: 'Clients' },
  { id: 'projects', label: 'Projects' },
  { id: 'team', label: 'Team' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'time-expense', label: 'Time & Expense' },
  { id: 'reports', label: 'Reporting' },
  { id: 'admin', label: 'Admin Center' },
  { id: '_section_spec', label: 'Specifications', section: true },
  { id: 'spec-masters', label: 'Spec Masters' },
  { id: 'smart-blocks', label: 'Smart Blocks' },
  { id: 'formas', label: 'Master Formas' },
  { id: '_section_tools', label: '', section: true },
  { id: 'format', label: 'Formatter' },
  { id: 'settings', label: 'Settings' },
] as const;

const VIEW_TITLES: Record<string, string | null> = {
  editor: null, // dynamic
  home: 'Home',
  clients: 'Clients',
  projects: 'Projects',
  team: 'Team',
  tasks: 'Tasks',
  'time-expense': 'Time & Expense',
  admin: 'Admin Center',
  'spec-masters': 'Spec Masters',
  'smart-blocks': 'Smart Blocks',
  formas: 'Master Formas',
  format: 'Formatter',
  compile: 'Add Specs',
  settings: 'Settings',
};

/* ═══════════ MAIN APP ═══════════ */
function App() {
  // ─── Auth State ───
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const prevFirmIdRef = useRef<string | null>(null);

  // ─── Core State ───
  const [themeMode, setThemeMode] = useState(() => {
    const saved = localStorage.getItem('sf-theme-mode');
    return saved === 'light' || saved === 'dark' || saved === 'system' ? saved : 'system';
  });
  const [resolvedDark, setResolvedDark] = useState(
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const [view, setView] = useState('home');
  // Deep-link payload — set by `navigate()` and consumed once by the
  // target page in a useEffect. Pages call `clearDeepLink()` after
  // applying so the same link doesn't re-trigger on re-render.
  const [deepLink, setDeepLink] = useState<DeepLink | null>(null);
  const navigate = (link: DeepLink) => {
    setDeepLink(link);
    setView(link.view);
  };
  const clearDeepLink = () => setDeepLink(null);

  // Activity log: fire a page.view event whenever the active view
  // changes. Coalesced server-side so re-mounts don't double-count.
  useEffect(() => {
    if (view) logPageView(view);
  }, [view]);
  const [sbOpen, setSbOpen] = useState(true);
  // Kept for the Formatter view's "last active forma" convenience default
  // (EditorComponents.tsx's Formatter, lastActiveId?: string | null) — never
  // set anywhere now that the classic editor is retired, so Formatter simply
  // falls back to settings.defaultTemplateId, same as it always did for a
  // fresh session.
  const [activeId] = useState<string | null>(null);
  // The Master Forma Studio (view === 'forma-studio') — which forma it has open.
  const [studioFormaId, setStudioFormaId] = useState<string | null>(null);
  // A brand-new, not-yet-saved forma (contract §3 R6, item 10a) — set together
  // with studioFormaId=null. CSI-seeded once per "New Forma" click so its
  // object identity stays stable for the life of the draft (re-seeding on
  // every render would blow away whatever the user just typed).
  const [studioNewSeed, setStudioNewSeed] = useState<StandardConfig | null>(null);
  const [standards, setStandards] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>({
    defaultTemplateId: '',
    exportDir: '',
    adminUsernames: ['jbeardsley', 'rfrick', 'dbronson', 'thead'],
  });
  const [toasts, setToasts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState({ username: '', displayName: '', role: 'user' });
  const [editorMasterId, setEditorMasterId] = useState<string | null>(null);
  const [editorProjectSpecId, setEditorProjectSpecId] = useState<string | null>(null);
  const [editorSourceProjectId, setEditorSourceProjectId] = useState<string | null>(null);
  // The Designer has no modes (docs/DESIGNER_UNIFIED_PLAN.md R5). Opening a
  // PROJECT FORMA still means "I came here to work on the paper", so the
  // workspace brings the formatting tools forward — the forma is styled
  // against a real spec of that project. A fresh ts re-applies the request.
  const [designerFormatIntent, setDesignerFormatIntent] = useState<{ ts: number } | null>(null);
  const editorDirtyRef = useRef(false);
  // The native File-menu Ctrl+S accelerator (main.ts's hidden menu) has no
  // idea what's open — whichever editor is currently mounted (the Studio or
  // the Designer) registers its own save-now trigger here; neither mounted
  // means Ctrl+S is a no-op, which is the correct behavior with no editor
  // open (ENGINEERING.md — preserve every function, never a silent dead key).
  const menuSaveTargetRef = useRef<(() => void) | null>(null);
  const exitGuardTriggerRef = useRef<{ show: () => void } | null>(null);
  const pendingNavRef = useRef<string | null>(null);
  // Mirror editor-open state so the one-shot app-close listener can read the
  // latest value without having to re-subscribe on every state change.
  const editorMasterIdRef = useRef<string | null>(null);
  const editorProjectSpecIdRef = useRef<string | null>(null);
  // editorModuleId / moduleEditorReturnRef removed in v2.12.0 — library
  // module feature retired (see migration 019).
  const [recoveryDrafts, setRecoveryDrafts] = useState<any[]>([]);
  const [updateInfo, setUpdateInfo] = useState<{
    status: 'checking' | 'up-to-date' | 'available' | 'downloading' | 'ready' | 'error';
    version?: string;
    progress?: number;
    error?: string;
  } | null>(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('');

  useEffect(() => {
    if (window.api?.getAppVersion)
      window.api
        .getAppVersion()
        .then((v: string) => setCurrentVersion(v))
        .catch(() => {});
  }, []);
  // Warn on window close/refresh if editor has unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (editorDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Block default OS file drag/drop on the window — Electron's default is to
  // navigate the renderer to file:// for the dropped file, which destroys the
  // app state. Component-level drop zones still receive the event because they
  // call stopPropagation before this listener runs.
  useEffect(() => {
    const swallow = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);
  useEffect(() => {
    editorMasterIdRef.current = editorMasterId;
  }, [editorMasterId]);
  useEffect(() => {
    editorProjectSpecIdRef.current = editorProjectSpecId;
  }, [editorProjectSpecId]);

  // openModuleFromEditor removed in v2.12.0 — library module feature retired.
  // OS close command (title-bar X, Alt+F4, Cmd+Q) — main prevents default and
  // asks us to run the exit-guard flow. If a spec editor is open we raise the
  // same Save / Submit / Discard / Cancel dialog the sidebar uses, marking the
  // pending destination as '__quit__' so the BlockEditor's onBack can route
  // through confirmAppClose() after the user picks an action.
  useEffect(() => {
    if (!window.api?.onAppCloseRequested) return;
    window.api.onAppCloseRequested(() => {
      const editorOpen = !!(editorMasterIdRef.current || editorProjectSpecIdRef.current);
      if (editorOpen && editorDirtyRef.current && exitGuardTriggerRef.current) {
        pendingNavRef.current = '__quit__';
        exitGuardTriggerRef.current.show();
      } else {
        window.api?.confirmAppClose?.();
      }
    });
  }, []);
  const [showRecovery, setShowRecovery] = useState(false);
  const [organizations, setOrganizations] = useState<any[]>([]);
  // Projects for the designer's attach-to-project picker (warmed with the cache)
  const [projectsAll, setProjectsAll] = useState<any[]>([]);
  // ─── Toast Helper ───
  const toast = (m: string, e?: boolean) => {
    const id = Date.now();
    setToasts((p) => [...p, { id, m, e }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3000);
  };

  // ─── Theme ───
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setResolvedDark(e.matches);
    mq.addEventListener('change', handler);
    setResolvedDark(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const isDark = themeMode === 'system' ? resolvedDark : themeMode === 'dark';
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);
  useEffect(() => {
    localStorage.setItem('sf-theme-mode', themeMode);
  }, [themeMode]);

  // App-level Ctrl+scroll zoom REMOVED (uniformity): the UI is responsive at 100%
  // and only the document canvas zooms (like Word). One layout, one set of ratios.
  // A stale saved zoom from the old system is cleared so nobody is stuck scaled.
  useEffect(() => {
    try {
      localStorage.removeItem('sf-app-zoom');
    } catch {}
  }, []);

  // ─── Auto-Updater Listener ───
  useEffect(() => {
    if (!window.api) return;
    setUpdateInfo({ status: 'checking' });
    window.api.onUpdateAvailable((_e: any, info: any) => {
      // Main auto-downloads in the background — no interruption here. The
      // dialog only appears when the update is READY (or on a manual check).
      setUpdateInfo({ status: 'downloading', version: info.version, progress: 0 } as any);
    });
    window.api.onUpdateDownloadProgress((_e: any, info: any) => {
      setUpdateInfo(
        (prev) => ({ ...prev, status: 'downloading', progress: Math.round(info.percent) }) as any
      );
    });
    window.api.onUpdateDownloaded(() => {
      setUpdateInfo((prev) => ({ ...prev, status: 'ready' }) as any);
      setShowUpdateDialog(true);
    });
    window.api.onUpdateUpToDate(() => {
      setUpdateInfo({ status: 'up-to-date' });
    });
    window.api.onUpdateError((_e: any, info: any) => {
      const msg = info?.message || 'Unable to check for updates';
      setUpdateInfo({ status: 'error', error: msg });
      // A background download can error (stall watchdog, network drop) while
      // the dialog was never open — surface it as a toast too, or the failure
      // is invisible (2026-08-13: this is the "frozen at 0%" report's real
      // dead end; the corner badge now also renders the error state below).
      toast(msg, true);
    });
  }, []);

  // ─── Set firm context + pre-warm cache (only invalidate on firm change) ───
  useEffect(() => {
    const newFirmId = authUser?.firmId || null;
    setCurrentFirmId(newFirmId);

    if (newFirmId) {
      // Connect live sync WebSocket for real-time updates
      connectLiveSync();

      // Push enabled modules to main process for IPC enforcement
      if (authUser!.enabledModules) {
        window.api.setFirmModules(authUser!.enabledModules).catch(() => {});
      }
      // Switch to firm-scoped file directories (specs/formas live on disk)
      window.api
        .switchFirmDb(newFirmId)
        .catch((e: unknown) => console.error('Failed to switch firm context:', e));

      // Only clear + re-warm when firmId actually changes
      if (prevFirmIdRef.current !== newFirmId) {
        prevFirmIdRef.current = newFirmId;
        clearAll();
        Prefs.reset();
        FirmPrefs.reset();
        setDataReady(false);
        // Pre-warm critical tables — fetch in small batches to avoid overwhelming the API
        const tables = [
          'team_members',
          'organizations',
          'projects',
          'roles',
          'billing_roles',
          'project_types',
          'billing_types',
          'activity_codes',
          'default_phases',
          'project_scopes',
          'teams',
          'team_assignments',
          'project_assignments',
          'overhead_categories',
          'expense_types',
          'tasks',
          'invitations',
          'standard_configs',
          'departments',
          'project_budgets',
          'dashboard_views',
        ];
        (async () => {
          // Batch in groups of 8 — server handles concurrent requests fine
          for (let i = 0; i < tables.length; i += 8) {
            const batch = tables.slice(i, i + 4);
            const results = await Promise.allSettled(batch.map((t) => list(t)));
            results.forEach((r, j) => {
              if (r.status === 'rejected')
                console.warn(`[app] Failed to load ${batch[j]}:`, r.reason?.message);
              else
                console.log(
                  `[app] Loaded ${batch[j]}: ${(r as PromiseFulfilledResult<any[]>).value.length} rows`
                );
            });
          }
          // Load formas after cache is warmed (firm context is set)
          try {
            const stds = await FormaService.list();
            setStandards(stds || []);
          } catch (err) {
            console.warn('[app] Failed to load formas:', err);
          }
          try {
            const orgs = await OrgService.list();
            setOrganizations(orgs);
          } catch (err) {
            reportError('app:loadOrgs', err);
          }
          try {
            const projs = await list('projects');
            setProjectsAll(projs || []);
          } catch (err) {
            reportError('app:loadProjects', err);
          }
          // Per-user UI prefs (table layouts) — synced across devices/web/
          // desktop. Fire-and-forget: DataTable applies them when they arrive;
          // boot must never wait on (or fail with) the prefs fetch.
          if (authUser?.id) void Prefs.load(authUser.id);
          // Firm-level UI defaults (workspace layouts a colleague published
          // for everyone who never customised — contract §2.9). Same
          // fire-and-forget rule: a firm with none simply uses out-of-box.
          if (newFirmId) void FirmPrefs.load(newFirmId);
          setDataReady(true);
        })();
      }
    } else {
      if (prevFirmIdRef.current !== null) {
        prevFirmIdRef.current = null;
        clearAll();
        Prefs.reset();
        FirmPrefs.reset();
        setDataReady(false);
      }
    }
  }, [authUser]);

  // ─── Auth: check saved JWT on mount, show login if none ───
  useEffect(() => {
    let authSub: { unsubscribe: () => void } | null = null;
    (async () => {
      const timeout = setTimeout(() => setAuthLoading(false), 5000);
      try {
        const u = await AuthService.getUser();
        setAuthUser(u);
        setAuthLoading(false);
        clearTimeout(timeout);
      } catch (e) {
        console.error('Auth check failed:', e);
        setAuthLoading(false);
        clearTimeout(timeout);
      }

      const {
        data: { subscription },
      } = AuthService.onAuthChange((u) => {
        // Only update auth user — firm context effect handles cache if firmId changes.
        // Do NOT invalidate cache here: token refreshes are routine and should be invisible.
        setAuthUser(u);
      });
      authSub = subscription;
    })();
    return () => {
      authSub?.unsubscribe();
    };
  }, []);

  // Re-fetch the auth user whenever the window regains focus or the role/team-member
  // tables change. This is how role edits (and module/tier changes) propagate to a
  // signed-in user without forcing them to sign out and back in. The /auth/me call
  // is cheap and the listener is scoped so it only fires when something relevant
  // actually changes, not on every cloud sync.
  useEffect(() => {
    if (!authUser) return;
    const refresh = async () => {
      try {
        const u = await AuthService.getUser();
        if (u) setAuthUser(u);
      } catch (err) {
        reportError('app:refreshAuthUser', err);
      }
    };
    const onSync = (e: Event) => {
      const tables: string[] = (e as CustomEvent).detail?.tables || [];
      if (tables.includes('roles') || tables.includes('team_members')) refresh();
    };
    window.addEventListener('focus', refresh);
    window.addEventListener('specforma-sync-complete', onSync);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('specforma-sync-complete', onSync);
    };
  }, [authUser?.id]);

  // (Realtime subscriptions removed — local PostgreSQL, no cloud sync)

  // ─── Crash Recovery: check for orphaned drafts once per sign-in ───
  // Keyed on userId so alt-tabbing (which refreshes authUser via /auth/me) does
  // NOT re-trigger the "Recover Unsaved Work" modal. It fires once when the
  // user signs in and never again during that session.
  const recoveryCheckedForUser = React.useRef<string | null>(null);
  useEffect(() => {
    if (authLoading || !authUser) return;
    if (recoveryCheckedForUser.current === authUser.id) return;
    recoveryCheckedForUser.current = authUser.id;
    (async () => {
      try {
        const drafts = await SpecService.listDrafts();
        if (drafts && drafts.length > 0) {
          setRecoveryDrafts(drafts);
          setShowRecovery(true);
        }
      } catch (err) {
        reportError('app:loadDrafts', err);
      }
    })();
  }, [authLoading, authUser?.id]);

  // ─── HAR Sync (RWB bridge): once per sign-in, after the cache pre-warm ───
  // Gated to Josh's account + desktop (canHarSync). Ref-keyed on userId so
  // window-focus /auth/me refreshes don't re-trigger it. Silent when no
  // workbook source is configured on this machine (plan comes back null).
  // WRITE-SAFE: the startup pass only COMPUTES the plan and notifies; it
  // writes nothing unless the explicit auto-apply pref is on.
  const harSyncRanForUser = React.useRef<string | null>(null);
  useEffect(() => {
    if (!dataReady || !authUser) return;
    if (!canHarSync(authUser)) return;
    if (harSyncRanForUser.current === authUser.id) return;
    harSyncRanForUser.current = authUser.id;
    (async () => {
      try {
        const plan = await computeHarSyncPlan();
        if (!plan || plan.total === 0) return;
        if (await getHarAutoApply()) {
          const res = await applyHarSyncPlan(plan);
          toast(
            `HAR sync applied: +${res.projectsAdded} projects, +${res.deliverablesAdded} deliverables, ` +
              `+${res.assignmentsAdded} assignments, ${res.forecastCellsSet} forecast cells`
          );
        } else {
          toast(
            `HAR sync: ${plan.total} pending additions — review in Admin Center › Data › HAR Sync`
          );
        }
      } catch (err) {
        console.warn('[harSync] startup check failed:', err);
      }
    })();
  }, [dataReady, authUser?.id]);

  useEffect(() => {
    async function init() {
      try {
        if (window.api) {
          const s = await window.api.loadSettings();
          if (s) setSettings(s);
          if (window.api.getCurrentUser) {
            const user = await window.api.getCurrentUser();
            if (user) setCurrentUser(user);
          }
        }
      } catch (err) {
        console.error('Init error:', err);
      }
      setLoading(false);
    }
    init();
  }, []);

  // Native File-menu accelerators (main.ts's hidden menu, Ctrl/Cmd+N and
  // +S) — forwarded over IPC since the menu itself has no idea what's on
  // screen. New always opens a fresh CSI-seeded forma in the Studio
  // (unconditional, same as the classic editor's Ctrl+N always did — "new
  // forma" isn't tied to what happens to be open). Save targets whichever
  // editor most recently registered a save-now trigger (the Studio or the
  // Designer, via registerSaveHandler); neither open, Ctrl+S is a no-op —
  // there is genuinely nothing to save.
  const handleNewStudioRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!window.api) return;
    const onNew = () => handleNewStudioRef.current();
    const onSave = () => menuSaveTargetRef.current?.();
    window.api.onMenuNew(onNew);
    window.api.onMenuSave(onSave);
    return () => {
      if (window.api?.removeMenuListeners) window.api.removeMenuListeners();
    };
  }, []);

  // "New Forma" from the Formas library opens the Studio, CSI-seeded
  // (contract §3 R6, item 10a).
  const handleNewStudio = () => {
    const seed: StandardConfig = {
      id: String(Date.now()),
      docSettings: mkDoc(),
      levels: csiLevelStyles(),
      headerFooter: mkHF(),
      special: mkSp(),
    };
    setStudioNewSeed(seed);
    setStudioFormaId(null);
    setView('forma-studio');
  };
  handleNewStudioRef.current = handleNewStudio;

  // "Import" from the Formas library opens the Studio with the harvested
  // draft (contract §3 R6 — same as the Studio's own Import… toolbar
  // action, useMasterFormaDraft.importDraft handles the rest once it
  // mounts). Gap-filled to CSI SectionFormat defaults, same as any import.
  const handleImportStudio = async () => {
    if (!window.api) {
      toast('Import requires desktop app', true);
      return;
    }
    const filePath = await window.api.openDotxDialog();
    if (!filePath) return;
    try {
      const result = await window.api.importTemplate(filePath);
      if (!result.success || !result.config) {
        toast('Import failed: ' + (result.error || 'Unknown error'), true);
        return;
      }
      setStudioNewSeed(fillFormaCsiGaps(result.config) as StandardConfig);
      setStudioFormaId(null);
      setView('forma-studio');
      toast('Imported — review and Save to add it to your Master Formas');
    } catch (err: any) {
      toast('Import error: ' + err.message, true);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await FormaService.delete(id);
      setStandards((p) => p.filter((s: any) => s.id !== id));
      toast('Deleted');
    } catch (err) {
      toast('Delete failed', true);
    }
  };

  const handleDup = async (id: string) => {
    const s = standards.find((x: any) => x.id === id);
    if (!s) return;
    const n = { ...JSON.parse(JSON.stringify(s)), id: String(Date.now()) };
    if (n.docSettings) n.docSettings.standardName += ' (Copy)';
    const dupUser = authUser?.displayName || currentUser.displayName || 'Unknown';
    n.editHistory = pushEditHistory([], dupUser, authUser?.id, 'duplicated');
    try {
      await FormaService.save(n);
      setStandards((p) => [...p, n]);
      toast('Duplicated');
    } catch {
      toast('Duplicate failed', true);
    }
  };

  const handleSettingsChange = async (s: any) => {
    setSettings(s);
    if (window.api) {
      await window.api.saveSettings(s);
    }
  };

  // Inline edit from the library grid (SharePoint-style): patch one docSettings
  // field (name/client), stamp history, persist — same store handleDup uses.
  const handleFormaFieldEdit = async (id: string, field: string, value: string) => {
    const s = standards.find((x: any) => x.id === id);
    if (!s) return;
    const n = JSON.parse(JSON.stringify(s));
    n.docSettings = { ...n.docSettings, [field]: value };
    const user = authUser?.displayName || currentUser.displayName || 'Unknown';
    n.editHistory = pushEditHistory(s.editHistory, user, authUser?.id, 'edited');
    try {
      await FormaService.save(n);
      setStandards((p) => p.map((x: any) => (x.id === id ? n : x)));
    } catch {
      toast('Save failed', true);
    }
  };

  // ─── Derived ───
  const viewTitle =
    view === 'spec-masters' && editorMasterId ? 'Spec Master Editor' : VIEW_TITLES[view] || '';

  const handleSignOut = async () => {
    disconnectLiveSync();
    await AuthService.signOut();
    setAuthUser(null);
  };

  // ─── Render ───

  // Auth loading spinner — tokens come from base.css (design-system layer)
  if (authLoading || (authUser && !dataReady))
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'var(--bg)',
          color: 'var(--tm)',
        }}
      >
        {authUser ? 'Loading data...' : 'Loading...'}
      </div>
    );

  // Desktop update dialog — defined BEFORE the auth gate so it renders on
  // the login screen too: updating must never require signing in.
  const updateDialogEl = (
    <>
      {/* ── Update Banner ── */}
      {/* Update dialog — modal */}
      {/* Auto-update dialog — desktop only; web updates ship with the site. */}
      {!isWeb && showUpdateDialog && updateInfo && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowUpdateDialog(false);
          }}
        >
          <div
            style={{
              background: 'var(--bg)',
              borderRadius: 12,
              border: '1px solid var(--bd)',
              width: 380,
              boxShadow: '0 20px 60px rgba(0,0,0,.25)',
              animation: 'si .2s',
            }}
          >
            <div style={{ padding: '20px 24px', textAlign: 'center' }}>
              {/* Icon */}
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  margin: '0 auto 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background:
                    updateInfo.status === 'up-to-date'
                      ? 'rgba(var(--okr),0.12)'
                      : updateInfo.status === 'error'
                        ? 'rgba(220,60,60,0.12)'
                        : 'rgba(var(--acr),0.12)',
                }}
              >
                {updateInfo.status === 'up-to-date' && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--ok)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                {updateInfo.status === 'error' && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--dg)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                )}
                {(updateInfo.status === 'available' ||
                  updateInfo.status === 'downloading' ||
                  updateInfo.status === 'ready') && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--ac)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                )}
                {updateInfo.status === 'checking' && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--ac)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ animation: 'spin 1s linear infinite' }}
                  >
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                )}
              </div>

              {/* Title */}
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                {updateInfo.status === 'checking' && 'Checking for Updates...'}
                {updateInfo.status === 'up-to-date' && 'App is Up to Date'}
                {updateInfo.status === 'available' && 'Update Available'}
                {updateInfo.status === 'downloading' && 'Downloading Update...'}
                {updateInfo.status === 'ready' && 'Update Ready'}
                {updateInfo.status === 'error' && 'Update Check Failed'}
              </div>

              {/* Subtitle */}
              <div style={{ fontSize: 13, color: 'var(--tm)', marginBottom: 16 }}>
                {updateInfo.status === 'checking' && 'Please wait...'}
                {updateInfo.status === 'up-to-date' &&
                  `You're running the latest version (v${currentVersion}).`}
                {updateInfo.status === 'available' && `v${updateInfo.version} is available.`}
                {updateInfo.status === 'downloading' &&
                  `v${updateInfo.version} — ${updateInfo.progress || 0}% complete`}
                {updateInfo.status === 'ready' &&
                  `v${updateInfo.version} is ready. Restart now, or it installs itself next time you quit.`}
                {updateInfo.status === 'error' &&
                  (updateInfo.error || 'Unable to check for updates.')}
              </div>

              {/* Progress bar */}
              {updateInfo.status === 'downloading' && (
                <div
                  style={{
                    height: 6,
                    borderRadius: 3,
                    background: 'var(--bd)',
                    overflow: 'hidden',
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: (updateInfo.progress || 0) + '%',
                      background: 'var(--ac)',
                      borderRadius: 3,
                      transition: 'width .3s',
                    }}
                  />
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                {updateInfo.status === 'available' && (
                  <button
                    onClick={() => {
                      setUpdateInfo(
                        (prev) => ({ ...prev, status: 'downloading', progress: 0 }) as any
                      );
                      window.api.downloadUpdate();
                    }}
                    style={{
                      padding: '8px 20px',
                      borderRadius: 6,
                      border: 'none',
                      background: 'var(--ac)',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Download Update
                  </button>
                )}
                {updateInfo.status === 'available' && (
                  <button
                    onClick={() => setShowUpdateDialog(false)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: '1px solid var(--bd)',
                      background: 'transparent',
                      color: 'var(--tm)',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Later
                  </button>
                )}
                {updateInfo.status === 'ready' && (
                  <button
                    onClick={async () => {
                      // Main pre-flights the installer file; {ok:false} means it
                      // vanished — heal by downloading again instead of dying.
                      const res = await window.api.installUpdate();
                      if (res && res.ok === false) {
                        setUpdateInfo(
                          (prev) => ({ ...prev, status: 'downloading', progress: 0 }) as any
                        );
                        window.api.downloadUpdate();
                      }
                    }}
                    style={{
                      padding: '8px 20px',
                      borderRadius: 6,
                      border: 'none',
                      background: 'var(--ac)',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Restart & Update
                  </button>
                )}
                {updateInfo.status === 'ready' && (
                  <button
                    onClick={() => setShowUpdateDialog(false)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: '1px solid var(--bd)',
                      background: 'transparent',
                      color: 'var(--tm)',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Later
                  </button>
                )}
                {updateInfo.status === 'error' && (
                  <button
                    onClick={() => {
                      setUpdateInfo({ status: 'checking' });
                      window.api.checkForUpdates();
                    }}
                    style={{
                      padding: '8px 20px',
                      borderRadius: 6,
                      border: 'none',
                      background: 'var(--ac)',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Retry
                  </button>
                )}
                {updateInfo.status !== 'ready' &&
                  updateInfo.status !== 'available' &&
                  updateInfo.status !== 'downloading' &&
                  updateInfo.status !== 'checking' && (
                    <button
                      onClick={() => setShowUpdateDialog(false)}
                      style={{
                        padding: '8px 20px',
                        borderRadius: 6,
                        border: '1px solid var(--bd)',
                        background: 'transparent',
                        color: 'var(--tm)',
                        fontSize: 13,
                        cursor: 'pointer',
                      }}
                    >
                      {updateInfo.status === 'error' ? 'Dismiss' : 'OK'}
                    </button>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Desktop browser-sign-in handoff (docs/AUTH_CLERK_PLAN.md): the exe opens
  // specforma.app/?desktop-signin=1 — that request renders ONLY the handoff
  // page (works whether or not a web session already exists).
  {
    const isWebBrowser = typeof navigator !== 'undefined' && !/electron/i.test(navigator.userAgent);
    if (isWebBrowser && isDesktopSigninRequest()) {
      return <DesktopHandoff />;
    }
  }

  // Logged-out entry. On the web (specforma.app) show the public marketing site
  // (Procore/SpecLink model: one domain = site + app); the desktop app goes
  // straight to the login screen, exactly as before (parity preserved).
  if (!authUser) {
    const isWebBrowser = typeof navigator !== 'undefined' && !/electron/i.test(navigator.userAgent);
    return isWebBrowser ? (
      <SpecFormaClerkProvider>
        {/* Clerk reloads the page after sign-in; this silent bridge finishes
            the Clerk→SpecForma exchange on any web load with a Clerk session. */}
        <WebClerkAutoAuth onAuth={setAuthUser} />
        <PublicSite onAuth={setAuthUser} />
      </SpecFormaClerkProvider>
    ) : (
      <>
        <LoginPage onAuth={setAuthUser} />
        {updateDialogEl}
      </>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--bg)',
        color: 'var(--tx)',
      }}
    >
      <style>{`
      @keyframes si{from{transform:translateY(-16px);opacity:0}to{transform:translateY(0);opacity:1}}
    `}</style>

      {/* ── Sidebar ── */}
      <div
        style={{
          width: sbOpen ? 200 : 56,
          minWidth: sbOpen ? 200 : 56,
          background: 'var(--sb)',
          color: 'var(--st)',
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid var(--sbd)',
          transition: 'all .25s',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            borderBottom: '1px solid var(--sbd)',
            minHeight: 50,
            justifyContent: sbOpen ? 'flex-start' : 'center',
          }}
        >
          <div
            style={{ cursor: 'pointer', flexShrink: 0, display: 'flex' }}
            onClick={() => setSbOpen(!sbOpen)}
          >
            {NAV_ICONS.menu}
          </div>
          {sbOpen && (
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: '-.02em',
                whiteSpace: 'nowrap',
              }}
            >
              SpecForma™
            </span>
          )}
        </div>
        <div
          style={{
            flex: 1,
            padding: '8px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {(() => {
            const moduleNavIds = authUser?.enabledModules
              ? getAllowedNavIds(authUser.enabledModules)
              : new Set(NAV_ITEMS.map((n: any) => n.id));
            return NAV_ITEMS.filter((n: any) => {
              // Web: the Formatter (local Word engine) is desktop-only — no nav
              // entry. Deep links to 'format' still land on DesktopOnlyNotice.
              if (isWeb && n.id === 'format') return false;
              // Module-based access: hide pages for disabled modules
              if (!n.section && !moduleNavIds.has(n.id)) return false;
              // Section headers: hide if all children are filtered out
              if (n.section) {
                const sectionIdx = NAV_ITEMS.indexOf(n);
                const nextSectionIdx = NAV_ITEMS.findIndex(
                  (item: any, i: number) => i > sectionIdx && item.section
                );
                const children = NAV_ITEMS.slice(
                  sectionIdx + 1,
                  nextSectionIdx === -1 ? undefined : nextSectionIdx
                );
                const hasVisibleChild = children.some(
                  (c: any) => !c.section && moduleNavIds.has(c.id)
                );
                if (!hasVisibleChild) return false;
              }
              // Role-based access: use granular permissions
              const up = authUser?.permissions || [];
              if (n.id === 'admin' && !up.includes('admin_center.view')) return false;
              if (
                n.id === 'reports' &&
                !up.includes('reports.view') &&
                !up.includes('admin_center.view')
              )
                return false;
              if (
                n.id === 'time-expense' &&
                !up.includes('timesheets.view_own') &&
                !up.includes('timesheets.create')
              )
                return false;
              if (n.id === 'team' && !up.includes('team_members.view')) return false;
              if (
                n.id === 'invoices' &&
                !up.includes('invoices.view_own') &&
                !up.includes('invoices.view_all') &&
                !up.includes('invoices.view') // legacy fallback
              )
                return false;
              return true;
            });
          })().map((n: any) => {
            if (n.section) {
              if (!n.label)
                return (
                  <div
                    key={n.id}
                    style={{ margin: '4px 12px', borderTop: '1px solid var(--sbd)' }}
                  />
                );
              return sbOpen ? (
                <div
                  key={n.id}
                  style={{
                    padding: '10px 16px 2px',
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--st)',
                    opacity: 0.4,
                    letterSpacing: '.08em',
                    textTransform: 'uppercase',
                    userSelect: 'none',
                  }}
                >
                  {n.label}
                </div>
              ) : (
                <div key={n.id} style={{ margin: '6px 12px', borderTop: '1px solid var(--sbd)' }} />
              );
            }
            return (
              <div
                key={n.id}
                onClick={() => {
                  // Spec editor is open AND has actual unsaved edits → force
                  // Save / Discard / Submit choice. If the editor is open but
                  // clean (just opened to view), let the user navigate away
                  // silently — no nag. (v2.11.11 — was unconditional in v2.5+.)
                  const editorOpen =
                    (editorMasterId && view === 'spec-masters') ||
                    (editorProjectSpecId && view === 'projects');
                  if (editorOpen && editorDirtyRef.current) {
                    pendingNavRef.current = n.id;
                    if (exitGuardTriggerRef.current) {
                      exitGuardTriggerRef.current.show();
                      return;
                    }
                  }
                  setView(n.id);
                  if (n.id !== 'spec-masters') setEditorMasterId(null);
                  if (n.id !== 'projects') setEditorProjectSpecId(null);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: sbOpen ? '10px 16px' : '10px 0',
                  cursor: 'pointer',
                  fontSize: 13,
                  borderRadius: 6,
                  margin: sbOpen ? '0 8px' : '0 4px',
                  whiteSpace: 'nowrap',
                  background: view === n.id ? 'var(--ach)' : 'transparent',
                  color: view === n.id ? 'var(--ac)' : 'var(--st)',
                  fontWeight: view === n.id ? 600 : 400,
                  transition: 'all .15s',
                  justifyContent: sbOpen ? 'flex-start' : 'center',
                }}
              >
                <span style={{ flexShrink: 0, display: 'flex' }}>{NAV_ICONS[n.id]}</span>
                {sbOpen && <span>{n.label}</span>}
              </div>
            );
          })}
        </div>
        {authUser && (
          <div style={{ borderTop: '1px solid var(--sbd)' }}>
            <NotificationBell
              memberId={authUser.id}
              collapsed={!sbOpen}
              onNavigateTask={() => setView('tasks')}
            />
          </div>
        )}
        <div
          style={{
            borderTop: '1px solid var(--sbd)',
            display: 'flex',
            flexDirection: sbOpen ? 'row' : 'column',
            alignItems: 'center',
          }}
        >
          <div
            onClick={() =>
              setThemeMode((m) => (m === 'light' ? 'system' : m === 'system' ? 'dark' : 'light'))
            }
            style={{
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              fontSize: 12,
              flex: 1,
              minWidth: 0,
              justifyContent: sbOpen ? 'flex-start' : 'center',
            }}
          >
            {isDark ? NAV_ICONS.sun : NAV_ICONS.moon}
            {sbOpen && (
              <span>
                {themeMode === 'system' ? 'System' : themeMode === 'dark' ? 'Dark' : 'Light'}
              </span>
            )}
          </div>
          {authUser && (
            <div
              style={{
                padding: sbOpen ? '12px 16px 12px 0' : '6px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                flexShrink: 0,
              }}
              title="Connected to cloud"
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  flexShrink: 0,
                  background: '#5cb85c',
                }}
              />
              {sbOpen && <span style={{ fontSize: 10, color: 'var(--tm)' }}>Cloud</span>}
            </div>
          )}
        </div>
        {currentVersion && (
          <div
            onClick={() => {
              // Web: version badge is display-only — updates ship with the site.
              if (isWeb) return;
              setShowUpdateDialog(true);
              if (window.api?.checkForUpdates) {
                setUpdateInfo({ status: 'checking' });
                window.api.checkForUpdates();
              }
            }}
            style={{
              padding: sbOpen ? '8px 16px' : '8px 0',
              fontSize: 11,
              color: updateInfo?.status === 'ready' ? 'var(--ac)' : 'var(--tm)',
              cursor: isWeb ? 'default' : 'pointer',
              fontFamily: "'JetBrains Mono', monospace",
              borderTop: '1px solid var(--sbd)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
              justifyContent: sbOpen ? 'flex-start' : 'center',
            }}
            title={isWeb ? undefined : 'Check for updates'}
          >
            {sbOpen && <span>v{currentVersion}</span>}
            {updateInfo?.status === 'available' && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '1px 5px',
                  borderRadius: 8,
                  background: '#f59e0b',
                  color: '#fff',
                  lineHeight: 1.2,
                  letterSpacing: '.02em',
                }}
              >
                {sbOpen ? 'UPDATE' : '!'}
              </span>
            )}
            {updateInfo?.status === 'downloading' && (
              <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--ac)' }}>
                {updateInfo.progress || 0}%
              </span>
            )}
            {updateInfo?.status === 'ready' && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '1px 5px',
                  borderRadius: 8,
                  background: 'var(--ac)',
                  color: '#fff',
                  lineHeight: 1.2,
                }}
              >
                {sbOpen ? 'RESTART' : '!'}
              </span>
            )}
            {updateInfo?.status === 'error' && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '1px 5px',
                  borderRadius: 8,
                  background: '#dc3c3c',
                  color: '#fff',
                  lineHeight: 1.2,
                }}
                title="Update failed — click to retry"
              >
                {sbOpen ? 'RETRY' : '!'}
              </span>
            )}
            {!sbOpen && !updateInfo?.status && (
              <span style={{ fontSize: 9, color: 'var(--tm)' }}>
                v{currentVersion.split('.').slice(0, 2).join('.')}
              </span>
            )}
          </div>
        )}
        {authUser && (
          <div
            style={{
              padding: '10px 16px',
              borderTop: '1px solid var(--sbd)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              justifyContent: sbOpen ? 'flex-start' : 'center',
            }}
          >
            {NAV_ICONS.user}
            {sbOpen && (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    color: 'var(--st)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {authUser.displayName}
                </span>
                <span
                  style={{ cursor: 'pointer', color: 'var(--tm)', fontSize: 10 }}
                  onClick={handleSignOut}
                >
                  Sign out
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Main Content ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {authUser && (
          <VerifyEmailBanner authUser={authUser} onVerified={() => window.location.reload()} />
        )}
        {/* Top bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 20px',
            background: 'var(--hd)',
            borderBottom: '1px solid var(--bd)',
            minHeight: 50,
            gap: 12,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600 }}>{viewTitle}</div>
        </div>

        {/* Page Content */}
        <div
          style={{
            flex: 1,
            overflow:
              view === 'spec-masters' ||
              view === 'compile' ||
              view === 'clients' ||
              view === 'projects'
                ? 'hidden'
                : 'auto',
          }}
        >
          {view === 'home' && <Home onNavigate={(v) => setView(v)} toast={toast} />}
          {view === 'clients' && <OrganizationsPage toast={toast} authUser={authUser} />}
          {view === 'projects' && !editorProjectSpecId && (
            <Projects
              toast={toast}
              authUser={authUser}
              onOpenProjectSpec={(specId: string, projectId: string) => {
                setDesignerFormatIntent(null);
                setEditorProjectSpecId(specId);
                setEditorSourceProjectId(projectId);
              }}
              onOpenForma={async (formaId: string) => {
                // Fresh read — the copy may have been forked seconds ago,
                // before the sync event refreshed `standards`.
                const all = await FormaService.list();
                const s = all.find((f: any) => f.id === formaId);
                if (!s) {
                  toast('Forma not found', true);
                  return;
                }
                // A PROJECT forma opens in THE DESIGNER's Design mode against
                // a real spec of its project — last opened, else first in CSI
                // order (docs/DESIGNER_PLAN.md R3). A MASTER forma opens in
                // the Studio (contract §3 R6 — parity proven, the classic
                // editor retired).
                if ((s as any).projectId) {
                  try {
                    const specs = await ProjectSpecService.listByProject((s as any).projectId);
                    if (specs.length > 0) {
                      let target = specs[0];
                      try {
                        const lastId = localStorage.getItem(
                          'sf.designer.lastSpec.' + (s as any).projectId
                        );
                        const last = specs.find((x: any) => x.id === lastId);
                        if (last) target = last;
                        else {
                          const digits = (x: any) => (x.csiNumber || '').replace(/\D/g, '');
                          target = [...specs].sort((a: any, b: any) =>
                            digits(a) < digits(b) ? -1 : digits(a) > digits(b) ? 1 : 0
                          )[0];
                        }
                      } catch {
                        /* localStorage unavailable — first in CSI order */
                      }
                      setDesignerFormatIntent({ ts: Date.now() });
                      setEditorSourceProjectId((s as any).projectId);
                      setEditorProjectSpecId(target.id);
                      return;
                    }
                    toast('This project has no specs yet — opening the forma editor');
                  } catch (err) {
                    console.warn('[designer] spec lookup failed, using forma editor:', err);
                  }
                }
                setStudioNewSeed(null);
                setStudioFormaId((s as any).id);
                setView('forma-studio');
              }}
              initialProjectId={editorSourceProjectId || undefined}
              navigate={navigate}
              deepLink={deepLink && deepLink.view === 'projects' ? deepLink : null}
              onDeepLinkConsumed={clearDeepLink}
            />
          )}
          {/* Project specs open in THE DESIGNER. BlockEditor is unreachable
              for project documents from here on (docs/DESIGNER_PLAN.md R1). */}
          {view === 'projects' && editorProjectSpecId && (
            <Designer
              projectSpecId={editorProjectSpecId}
              onOpenSection={(specId: string) => setEditorProjectSpecId(specId)}
              onManageSections={() => setEditorProjectSpecId(null)}
              onBack={() => {
                editorDirtyRef.current = false;
                setEditorProjectSpecId(null);
                if (pendingNavRef.current === '__quit__') {
                  pendingNavRef.current = null;
                  window.api?.confirmAppClose?.();
                  return;
                }
                if (pendingNavRef.current) {
                  setEditorSourceProjectId(null);
                  setView(pendingNavRef.current);
                  pendingNavRef.current = null;
                }
              }}
              toast={toast}
              userName={authUser?.displayName || 'user'}
              canEdit={
                hasPermission(authUser?.permissions || [], 'project_specs.edit_any') ||
                hasPermission(authUser?.permissions || [], 'project_specs.edit_own')
              }
              isDark={isDark}
              userId={authUser?.id}
              openFormatting={designerFormatIntent}
              registerSaveHandler={(fn) => {
                menuSaveTargetRef.current = fn;
              }}
              canIssue={hasPermission(authUser?.permissions || [], 'project_specs.create_issuance')}
              canSetFirmDefault={hasPermission(
                authUser?.permissions || [],
                'admin_center.manage_firm_settings'
              )}
            />
          )}
          {view === 'team' && (
            <TeamPage
              toast={toast}
              authUser={authUser}
              deepLink={deepLink && deepLink.view === 'team' ? deepLink : null}
              onDeepLinkConsumed={clearDeepLink}
            />
          )}
          {view === 'tasks' && <TasksPage toast={toast} authUser={authUser} />}
          {view === 'time-expense' && (
            <TimeExpensePage
              toast={toast}
              authUser={authUser}
              deepLink={deepLink && deepLink.view === 'time-expense' ? deepLink : null}
              onDeepLinkConsumed={clearDeepLink}
            />
          )}

          {view === 'invoices' && (
            <InvoicesPageNew
              toast={toast}
              authUser={authUser}
              deepLink={deepLink && deepLink.view === 'invoices' ? deepLink : null}
              onDeepLinkConsumed={clearDeepLink}
            />
          )}
          {view === 'reports' && <ReportsPageNew toast={toast} authUser={authUser} />}
          {view === 'admin' && (
            <AdminPage
              toast={toast}
              userPermissions={authUser?.permissions}
              userEmail={authUser?.email}
            />
          )}
          {view === 'formas' && (
            <Library
              standards={standards.filter((s: any) => !s.projectId)}
              defaultId={settings.defaultTemplateId}
              // Every master forma opens in the Studio (contract §3 R6 —
              // parity proven, the classic editor retired).
              onLoad={(s: any) => {
                setStudioNewSeed(null);
                setStudioFormaId(s.id);
                setView('forma-studio');
              }}
              onCreate={handleNewStudio}
              onImport={handleImportStudio}
              onDup={handleDup}
              onDel={handleDelete}
              onFieldEdit={handleFormaFieldEdit}
            />
          )}

          {view === 'forma-studio' && (studioFormaId || studioNewSeed) && (
            <Studio
              formaId={studioFormaId}
              newForma={studioNewSeed}
              onBack={() => {
                setStudioFormaId(null);
                setStudioNewSeed(null);
                setView('formas');
              }}
              organizations={organizations}
              projects={projectsAll}
              onFormaPersisted={(f: any) =>
                setStandards((p) => {
                  const x = p.findIndex((s: any) => s.id === f.id);
                  if (x >= 0) {
                    const n = [...p];
                    n[x] = f;
                    return n;
                  }
                  return [...p, f];
                })
              }
              registerSaveHandler={(fn) => {
                menuSaveTargetRef.current = fn;
              }}
              canSetFirmDefault={hasPermission(
                authUser?.permissions || [],
                'admin_center.manage_firm_settings'
              )}
              toast={toast}
              isDark={isDark}
              userId={authUser?.id}
              userName={authUser?.displayName || currentUser.displayName || 'Unknown'}
            />
          )}

          {view === 'format' &&
            (isWeb ? (
              <DesktopOnlyNotice />
            ) : (
              <Formatter
                standards={standards.filter((s: any) => !s.projectId)}
                lastActiveId={activeId}
                settings={settings}
                onSettingsChange={handleSettingsChange}
              />
            ))}
          {view === 'spec-masters' && !editorMasterId && (
            <SpecLibrary
              onOpenEditor={(id: string) => {
                setEditorMasterId(id);
              }}
              toast={toast}
              userRole={authUser?.role || 'member'}
              userPermissions={authUser?.permissions || []}
              userName={authUser?.displayName || 'user'}
            />
          )}
          {view === 'spec-masters' && editorMasterId && (
            <BlockEditor
              masterId={editorMasterId}
              onBack={() => {
                editorDirtyRef.current = false;
                setEditorMasterId(null);
                if (pendingNavRef.current === '__quit__') {
                  pendingNavRef.current = null;
                  window.api?.confirmAppClose?.();
                  return;
                }
                if (pendingNavRef.current) {
                  setView(pendingNavRef.current);
                  pendingNavRef.current = null;
                }
              }}
              toast={toast}
              userRole={authUser?.role || 'member'}
              userPermissions={authUser?.permissions || []}
              userName={authUser?.displayName || 'user'}
              onSavedAs={(newId) => {
                setEditorMasterId(newId);
              }}
              parentDirtyRef={editorDirtyRef}
              exitGuardTriggerRef={exitGuardTriggerRef}
              onCancelExit={() => {
                pendingNavRef.current = null;
              }}
              onOpenSmartBlocksLibrary={() => setView('smart-blocks')}
            />
          )}
          {view === 'compile' && (
            <AddSpecs
              toast={toast}
              standards={standards}
              deepLink={deepLink && deepLink.view === 'compile' ? deepLink : null}
              onDeepLinkConsumed={clearDeepLink}
              onExit={(projectId?: string) => {
                if (projectId) setEditorSourceProjectId(projectId);
                setView('projects');
              }}
              onOpenProjectDashboard={(projectId: string) => {
                setEditorSourceProjectId(projectId);
                setView('projects');
              }}
            />
          )}
          {view === 'smart-blocks' && (
            <SmartBlocksPage
              toast={toast}
              userName={authUser?.displayName}
              userId={authUser?.id}
              userPermissions={authUser?.permissions}
            />
          )}
          {view === 'settings' && (
            <Settings
              themeMode={themeMode}
              onThemeMode={setThemeMode}
              standards={standards}
              settings={settings}
              onSettingsChange={handleSettingsChange}
              currentUser={currentUser}
            />
          )}
        </div>
      </div>

      {/* ── Crash Recovery Modal ── */}
      {showRecovery && recoveryDrafts.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              background: 'var(--sf)',
              borderRadius: 12,
              padding: 24,
              width: 460,
              maxWidth: '92%',
              border: '1px solid var(--bd)',
              boxShadow: '0 8px 32px rgba(0,0,0,.25)',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
              Recover Unsaved Work
            </div>
            <p style={{ fontSize: 13, color: 'var(--tm)', marginBottom: 16 }}>
              {recoveryDrafts.length === 1
                ? 'An unsaved draft was found'
                : `${recoveryDrafts.length} unsaved drafts were found`}{' '}
              from a previous session. Would you like to resume editing?
            </p>
            <div
              style={{
                maxHeight: 200,
                overflow: 'auto',
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {recoveryDrafts.map((d: any) => (
                <div
                  key={d.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    background: 'var(--sa)',
                    borderRadius: 6,
                    border: '1px solid var(--bd)',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {d.title || d.csiNumber || 'Untitled Draft'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--tm)' }}>
                      Last saved: {d.lastSaved ? new Date(d.lastSaved).toLocaleString() : 'Unknown'}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setView('spec-masters');
                      setEditorMasterId(d.blueprintId);
                      setShowRecovery(false);
                    }}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 5,
                      border: 'none',
                      background: 'var(--ac)',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Resume
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={async () => {
                  for (const d of recoveryDrafts) {
                    try {
                      await SpecService.discardDraft(d.id);
                    } catch (e) {
                      console.error('Failed to discard draft during recovery:', e);
                    }
                  }
                  setRecoveryDrafts([]);
                  setShowRecovery(false);
                  toast('Drafts discarded');
                }}
                style={{
                  padding: '6px 16px',
                  borderRadius: 5,
                  border: '1px solid var(--bd)',
                  background: 'transparent',
                  color: 'var(--tm)',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Discard All
              </button>
              <button
                onClick={() => setShowRecovery(false)}
                style={{
                  padding: '6px 16px',
                  borderRadius: 5,
                  border: 'none',
                  background: 'var(--ac)',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {updateDialogEl}

      {/* ── Toasts ── */}
      <div
        style={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {toasts.map((t: any) => (
          <div
            key={t.id}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              color: '#fff',
              background: t.e ? 'var(--dg)' : 'var(--ac)',
              boxShadow: '0 4px 16px rgba(0,0,0,.2)',
              animation: 'si .3s',
            }}
          >
            {t.m}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Mount ───
// One TooltipProvider for the whole app so <Hint> works anywhere (including
// the login/marketing early-return branches). 250ms is Radix's "explain on
// hover, don't nag" default band — fast enough to feel instant on purpose,
// slow enough that sweeping the mouse across a palette shows nothing.
const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
  <TooltipProvider delayDuration={250} skipDelayDuration={400}>
    <App />
  </TooltipProvider>
);
